import { useState } from "react";
import { InputNumber } from "primereact/inputnumber";
import ButtonSpinner from "../../controls/ButtonSpinner";
import {
  getSourceItemParentQuantity,
  getSourceItemUnitLabel,
  sumSourceItemSplitQuantities,
} from "../../../shared/utility";

export default function OrderSourceItemPriceSplitModal(props) {
  const {
    sourceItem,
    split,
    onConfirm,
    setOpen,
    showErrorModal,
  } = props;

  const isEdit = Boolean(split);
  const unitLabel = getSourceItemUnitLabel(sourceItem.selling_unit_of_measure);
  const parentQty = getSourceItemParentQuantity(sourceItem);
  const allocatedQty = sumSourceItemSplitQuantities(
    sourceItem.price_splits,
    isEdit ? split.id : null,
  );
  const availableQty = parentQty - allocatedQty;
  const isPieceUnit = parseInt(sourceItem.selling_unit_of_measure, 10) === 3;

  const [quantity, setQuantity] = useState(
    isEdit ? parseFloat(split.quantity) : null,
  );
  const [unitPrice, setUnitPrice] = useState(
    isEdit ? parseFloat(split.unit_price) : null,
  );
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const productName =
    sourceItem.manufactured_product?.product?.name || "Product";

  const handleSubmit = async () => {
    const qty = parseFloat(quantity);
    const price = parseFloat(unitPrice);

    if (!qty || qty <= 0) {
      showErrorModal("Invalid quantity", "Split quantity must be greater than zero.");
      return;
    }

    if (Number.isNaN(price) || price < 0) {
      showErrorModal("Invalid price", "Unit price cannot be negative.");
      return;
    }

    if (qty > availableQty) {
      showErrorModal(
        "Quantity too high",
        "Split quantities cannot exceed source item quantity.",
      );
      return;
    }

    setSubmitInProgress(true);
    try {
      const result = await onConfirm({
        quantity: qty,
        unit_price: price,
      });

      if (result?.success) {
        setOpen({ open: false });
      }
    } finally {
      setSubmitInProgress(false);
    }
  };

  return (
    <div className="justify-between">
      <div>
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {isEdit ? "Edit price split" : "Add price split"}
            </h3>
            <p className="mt-1 text-sm text-gray-600">{productName}</p>
          </div>
        </div>

        <div className="py-3 gap-4 bg-white sm:rounded-xl">
          <div className="mb-4 text-sm text-gray-700 space-y-1">
            <p>
              Total: {parentQty.toFixed(isPieceUnit ? 0 : 2)} {unitLabel}
            </p>
            <p>
              Available: {availableQty.toFixed(isPieceUnit ? 0 : 2)} {unitLabel}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 w-full">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity ({unitLabel})
              </label>
              <InputNumber
                value={quantity}
                onValueChange={(e) => setQuantity(e.value)}
                minFractionDigits={isPieceUnit ? 0 : 2}
                maxFractionDigits={isPieceUnit ? 0 : 2}
                className="w-full"
                inputClassName="w-full"
                disabled={submitInProgress}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit price
              </label>
              <InputNumber
                value={unitPrice}
                onValueChange={(e) => setUnitPrice(e.value)}
                mode="currency"
                currency="USD"
                locale="en-US"
                minFractionDigits={2}
                maxFractionDigits={4}
                className="w-full"
                inputClassName="w-full"
                disabled={submitInProgress}
              />
            </div>
          </div>

          <div className="sm:flex-no-wrap my-auto flex items-center">
            <button
              type="button"
              disabled={submitInProgress}
              onClick={handleSubmit}
              className="inline-flex justify-center mt-3 py-35-custom px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center disabled:opacity-50"
            >
              <ButtonSpinner
                spin={submitInProgress}
                text={isEdit ? "Save" : "Add"}
              />
            </button>
            <button
              type="button"
              disabled={submitInProgress}
              onClick={() => setOpen({ open: false })}
              className="ml-2 inline-flex justify-center mt-3 py-35-custom px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 items-center disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
